import React, { useState, useEffect } from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { supabase } from './services/supabaseClient';
import { SpeedInsights } from "@vercel/speed-insights/react";
import { Toaster } from 'sonner';
import LoginView from './components/LoginView';
import RegisterView from './components/RegisterView';
import Dashboard from './components/Dashboard';
import LandingPage from './components/LandingPage';
import AuthCallback from './components/AuthCallback';
import AdminDashboard from './components/AdminDashboard';
import PendingApprovalView from './components/PendingApprovalView';

const App: React.FC = () => {
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isApproved, setIsApproved] = useState<boolean>(false);
  const [profileData, setProfileData] = useState<any>(null);

  useEffect(() => {
    const fetchProfileStatus = async (userId: string, email?: string) => {
      // BYPASS ADMIN SUPREM
      if (email === 'madjo9@gmail.com') {
        setIsApproved(true);
        setProfileData({ is_approved: true, name: 'Admin Suprem', bar: 'LexSmart', bar_card_number: 'ADMIN' });
        return;
      }

      // Folosim maybeSingle() în loc de single() pentru a nu genera eroare HTTP 406 
      // în caz că rândul lipsește (ex: cont șters manual din baza de date)
      const { data, error } = await supabase.from('profiles').select('*').eq('id', userId).maybeSingle();
      if (!error && data) {
        setIsApproved(data.is_approved || false);
        setProfileData(data);
      }
    };

    // Verificăm sesiunea inițială
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session?.user?.id) {
        fetchProfileStatus(session.user.id, session.user.email).finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    });

    // Ascultăm schimbările de autentificare
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session?.user?.id) {
        fetchProfileStatus(session.user.id, session.user.email);
      } else {
        setIsApproved(false);
        setProfileData(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-[#020617]">
        <div className="w-12 h-12 border-4 border-indigo-600/20 border-t-indigo-600 rounded-full animate-spin mb-4"></div>
        <p className="text-indigo-500/50 font-black text-[10px] uppercase tracking-widest italic">Sincronizare Cabinet...</p>
      </div>
    );
  }

  return (
    <>
      <Toaster position="top-center" richColors />
      <HashRouter>
        <Routes>
          {/* Ruta pentru procesarea confirmărilor email */}
          <Route path="/auth/callback" element={<AuthCallback />} />
          
          <Route path="/login" element={!session ? <LoginView onLogin={() => {}} /> : <Navigate to="/dashboard" />} />
          <Route path="/register" element={!session ? <RegisterView /> : <Navigate to="/dashboard" />} />
          <Route path="/dashboard/*" element={session ? 
            (isApproved ? (
              <Dashboard 
                user={{ 
                  email: session.user.email,
                  name: profileData?.name || session.user.user_metadata?.name || 'Avocat',
                  bar: profileData?.bar || session.user.user_metadata?.bar,
                  isApproved: true,
                  barCardNumber: profileData?.bar_card_number || session.user.user_metadata?.barCardNumber
                }} 
                onLogout={() => supabase.auth.signOut()} 
                onUpdateUser={async (data) => {
                  try {
                    await supabase.auth.updateUser({ data });
                    setSession((prev: any) => prev ? {
                      ...prev,
                      user: { ...prev.user, user_metadata: { ...prev.user.user_metadata, ...data } }
                    } : prev);
                  } catch (err) { console.error("Eroare actualizare profil:", err); }
                }}
                onAddReport={(report) => {
                  console.info("Sesizare nouă:", report);
                }}
              />
            ) : (
              <PendingApprovalView 
                user={{ 
                  email: session.user.email,
                  name: profileData?.name || session.user.user_metadata?.name || 'Avocat',
                  bar: profileData?.bar || session.user.user_metadata?.bar,
                  isApproved: false,
                  barCardNumber: profileData?.bar_card_number || session.user.user_metadata?.barCardNumber
                }} 
                onLogout={() => supabase.auth.signOut()} 
              />
            )) : <Navigate to="/login" />} 
          />
          <Route path="/" element={<LandingPage />} />
          <Route path="/admin" element={<AdminDashboard />} />
          {/* Fallback pentru rute inexistente */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </HashRouter>
      <SpeedInsights />
    </>
  );
};

export default App;